import { CoreFetch } from '../../api/index';
import { BankingApi, ListPlaidItemTransactionsOrderEnum } from '../../api/server/apis/BankingApi';
import { ProviderPlaidItemTransaction } from '../../api/server/models/ProviderPlaidItemTransaction';
import { ProviderPlaidItemTransactionSortType } from '../../api/server/models/ProviderPlaidItemTransactionSortType';
import { amountOf } from './remit-adjustments';

/**
 * The bank side of Reconcile (FE-163, crosswalk §4): the practice's connected bank items and their deposits.
 *
 * ## One implementation for both consoles
 *
 * There is no `/support/banking/*`. A Support login reads the provider banking endpoints for the account Support has
 * switched to — measured on production 2026-10-09: the same item and the same 6,411 transactions as the provider login.
 *
 * ## Tabs are filters, spelled exactly
 *
 * A transaction carries no posted or ignored flag of its own; the tabs are the `filterPosted` / `filterActive` queries.
 * `EQ:1` and `EQ:0` partition the list (1,976 posted + 4,435 unposted = 6,411, measured 2026-10-09). A bare `1` or
 * `EQ:true` is silently ignored here and returns every transaction — so the spelling is fixed in one place.
 *
 * ## Money in is NEGATIVE
 *
 * Plaid's sign: an inflow is negative. Measured 2026-10-09: all 30 sampled posted transactions equal MINUS their remit's
 * paid amount, and Unposted holds outflows too (4,435 = 2,347 in + 2,088 out). Only money in can pay a remit, so
 * Unposted lists deposits alone (`filterAmount=LT:0`, the design's "unposted deposits"), each row carries its deposit as a
 * positive figure, and a typed amount is asked for as the negative it is stored as. One value only: repeated values of
 * one filter are OR'ed, so `LT:0` with `EQ:-890.82` would return every deposit.
 */

export type TransactionTab = 'UNPOSTED' | 'POSTED' | 'IGNORED';
export type TransactionSort = 'DATE' | 'AMOUNT' | 'NAME' | 'CREATED';

/** One connected bank item, as the account picker shows it. */
export interface IBankAccount {
    /** The Plaid item — what every transaction call takes. */
    itemId: string;
    /** `JPMorgan Chase Operating ••4471`: the institution and account name; a manual item is its account name alone. */
    label: string;
    institution?: string;
    accountName?: string;
    mask?: string;
    itemType?: string;
}

export interface IBankTransaction {
    id: string;
    /** The bank's own transaction id — what the page shows as Transaction ID and the remit shows once posted. */
    externalId?: string;
    /** The bank's description, as sent. */
    name?: string;
    /** As stored: negative is money in. */
    amount: number;
    /** Money in as a positive figure — what the page shows and what a remit's paid amount is compared with. */
    deposit: number;
    date?: string;
    itemId: string;
    /** The payment number the deposit carries, when its description names one (FE-183): what a remit shows as `#…`. */
    paymentNumber?: string;
}

export interface ITransactionQuery {
    tab: TransactionTab;
    limit: number;
    offset: number;
    name?: string;
    /** Exactly this deposit, as typed (positive). */
    amount?: string;
    /** A deposit within this band, both ends included (positive) — Reconcile flipped, matching a remit within $5. */
    amountBetween?: [string, string];
    /** These transactions only (repeated `EQ:` — an OR, which is what this wants). */
    ids?: string[];
    /** On this day, `yyyy-MM-dd`. */
    date?: string;
    /** Within these days, both included — one `BT:` value (repeated bounds are OR'ed by the API). */
    dateBetween?: [string, string];
    externalId?: string;
    sort: TransactionSort;
    order: 'ASCENDING' | 'DESCENDING';
}

export interface IBankingApi {
    listAccounts(): Promise<IBankAccount[]>;
    listTransactions(itemId: string, query: ITransactionQuery): Promise<{ rows: IBankTransaction[]; total: number }>;
    /** Leaves the deposit out of Unposted (crosswalk §4: `…/transaction/disable`). */
    ignore(itemId: string, transactionId: string): Promise<void>;
    restore(itemId: string, transactionId: string): Promise<void>;
}

const TAB_FILTERS: Record<TransactionTab, { filterPosted?: string; filterActive?: string }> = {
    UNPOSTED: { filterPosted: 'EQ:0', filterActive: 'EQ:1' },
    POSTED: { filterPosted: 'EQ:1' },
    IGNORED: { filterActive: 'EQ:0' },
};

const SORTS: Record<TransactionSort, ProviderPlaidItemTransactionSortType> = {
    DATE: ProviderPlaidItemTransactionSortType.DATE,
    AMOUNT: ProviderPlaidItemTransactionSortType.AMOUNT,
    NAME: ProviderPlaidItemTransactionSortType.NAME,
    CREATED: ProviderPlaidItemTransactionSortType.CREATED,
};

const toTransaction = (itemId: string) => (row: ProviderPlaidItemTransaction): IBankTransaction => {
    const amount = amountOf(row.amount);
    return {
        id: row.id || '',
        externalId: row.externalId,
        name: row.name,
        amount,
        deposit: -amount,
        date: row.date,
        itemId,
        paymentNumber: depositPaymentNumber(row.name),
    };
};

/**
 * The amount filter: the typed deposit as stored, a band of deposits, or — on Unposted — money in only. Never two of them
 * (repeated values OR). Plaid stores money in as negative, so a band of deposits is one `BT:` over the negated amounts,
 * kept above zero so it never takes in an outgoing payment.
 */
export const transactionAmountFilter = (
    tab: TransactionTab,
    typed: string | undefined,
    between?: [string, string],
): string[] | undefined => {
    const deposit = Number(typed);
    if (typed && Number.isFinite(deposit)) {
        return [`EQ:${(-deposit).toFixed(2)}`];
    }
    const [low, high] = (between || []).map(Number);
    if (between && Number.isFinite(low) && Number.isFinite(high) && high > 0) {
        return [`BT:${(-high).toFixed(2)}:${(-Math.max(low, 0.01)).toFixed(2)}`];
    }
    return tab === 'UNPOSTED' ? ['LT:0'] : undefined;
};

/**
 * The deposit's payer, for the row's bold line — display only (crosswalk §4, "parsed from `name`"). Two shapes name it
 * outright, and only those are read; anything else shows the description as sent rather than a guess:
 *
 * - an ACH credit's originator field, `ORIG CO NAME:HUMANA INS CO ORIG ID:…` (2026-10-09) or, on another bank's feed,
 *   `ORIG CO NAME=PGBA TRICARE WR7;ORIG ID=…` (2026-10-15);
 * - `Check Deposit From Horizon`, the design's example.
 */
export const depositPayer = (name: string | undefined): string | undefined => {
    const text = (name || '').trim();
    const ach = /ORIG CO NAME[:=]\s*(.+?)\s*;?\s*ORIG ID[:=]/i.exec(text);
    if (ach) {
        return ach[1].trim() || undefined;
    }
    const check = /\bdeposit from\s+(.+)$/i.exec(text);
    return check ? check[1].trim() : undefined;
};

/**
 * The payment number an insurer's ACH payment carries (FE-183). A CCD+ entry's addenda holds the 835's reassociation trace,
 * `TRN*1*{TRN02}*{TRN03}`, and TRN02 is the check/EFT number the remit reports as its payment number. Measured on
 * production 2026-10-16: 70% of money-in descriptions carry it, and on 142 of 142 deposits posted to an ERA it equals
 * that ERA's `paymentNumber` exactly. No other shape is read: a description without it has no number, never a guess (NS-3).
 *
 * The whole trace or nothing: TRN03 must follow, so a description the bank cut short inside TRN02 shows no number rather
 * than part of one, and no field takes in a terminator (`~`, `\`, `;`). Measured 2026-10-16 on 3,000 money-in rows: all
 * 2,401 that carry a trace carry it whole, so this drops none of them.
 */
export const depositPaymentNumber = (name: string | undefined): string | undefined => {
    const trn = /TRN\*1\*([^*\\;~]+)\*[^*\\;~\s]+/.exec(name || '');
    return trn ? trn[1].trim() || undefined : undefined;
};

export const bankingApi: IBankingApi = {
    listAccounts: async () => {
        const items = await CoreFetch<BankingApi>(BankingApi).listPlaidItems();
        return items.map(item => {
            // The transactions come per item, not per account. With several accounts in one item, any one account's name
            // and mask would mislabel the others' rows (and their CSV lines), so such an item is named by its bank only.
            // Measured on production 2026-10-14: the practice checked has one item with one account.
            const accounts = item.accounts || [];
            const account = accounts.length === 1 ? accounts[0] : undefined;
            const manual = String(item.itemType).toUpperCase() === 'MANUAL';
            const name = [manual ? undefined : item.institutionName, account?.name].filter(Boolean).join(' ');
            return {
                itemId: item.plaidItemId || '',
                label: [name || item.institutionName || 'Bank account', account?.mask ? `••${account.mask}` : '']
                    .filter(Boolean)
                    .join(' '),
                institution: item.institutionName,
                accountName: account?.name,
                mask: account?.mask,
                itemType: item.itemType,
            };
        });
    },
    listTransactions: async (itemId, query) => {
        const response = await CoreFetch<BankingApi>(BankingApi).listPlaidItemTransactionsRaw({
            id: itemId,
            limit: query.limit,
            offset: query.offset,
            ...TAB_FILTERS[query.tab],
            filterName: query.name ? [`LK:${query.name}`] : undefined,
            filterAmount: transactionAmountFilter(query.tab, query.amount, query.amountBetween),
            filterId: query.ids?.length ? query.ids.map(id => `EQ:${id}`) : undefined,
            filterDate: query.date
                ? [`EQ:${query.date}`]
                : query.dateBetween
                ? [`BT:${query.dateBetween[0]}:${query.dateBetween[1]}`]
                : undefined,
            filterExternalId: query.externalId ? [`EQ:${query.externalId}`] : undefined,
            // One key: unlike the remit lists, this list ignores every sort key after the first (measured on production
            // 2026-10-12), so ties can still skip a row between pages until the backend's own tie-breaker lands.
            sort: [SORTS[query.sort]],
            order: [
                query.order === 'ASCENDING'
                    ? ListPlaidItemTransactionsOrderEnum.ASCENDING
                    : ListPlaidItemTransactionsOrderEnum.DESCENDING,
            ],
        });
        const rows = await response.value();
        const header = Number(response.raw.headers.get('pagination-total'));
        return {
            rows: rows.map(toTransaction(itemId)),
            total: response.raw.headers.has('pagination-total') && Number.isFinite(header) ? header : rows.length,
        };
    },
    // The path's `{id}` is the TRANSACTION here, though it sits where the list call puts the item ("The plaid item transaction
    // id" in the spec). Measured on production 2026-10-12: disable with a transaction id ignored exactly that transaction
    // (ignored 0 → 1), and enable restored it (→ 0).
    ignore: async (_itemId, transactionId) => {
        await CoreFetch<BankingApi>(BankingApi).disableProviderPlaidItemTransaction({ id: transactionId });
    },
    restore: async (_itemId, transactionId) => {
        await CoreFetch<BankingApi>(BankingApi).enableProviderPlaidItemTransaction({ id: transactionId });
    },
};
